import { Suspense, useRef, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { OrbitControls, Stats, Html } from '@react-three/drei';
import * as THREE from 'three';
import { useThree, useFrame } from '@react-three/fiber';
import { ThreeDModels } from '@/lib/threejs/config';
import { GridBase } from './grid-base';
import { ModelLoader } from '../models/model-loader';
import { getSensorAttachmentPoint } from './model-sensor-points';
import { InfoPanel } from './info-panel';
import { AlertsPanel } from './alerts-panel';
import { useGetAlertsQuery, useGetAnalyticsTimeSeriesQuery } from '@/services';
import { DeviceCubes } from './device-cubes';
import { Eye, EyeOff } from 'lucide-react';

function ModelLoadingFallback() {
  return (
    <group>
      <mesh>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="hotpink" wireframe />
      </mesh>
      <Html center>
        <div className="bg-background/80 backdrop-blur-sm p-2 rounded-md border text-xs">Loading model...</div>
      </Html>
    </group>
  );
}

interface SceneProps {
  sensorData: Array<{
    id: string;
    name: string;
    type: string;
    value: number;
    isOnline: boolean;
    unit: string;
    position: [number, number, number];
    deviceName?: string;
    deviceId?: string;
    timestamp?: string;
    history?: Array<{ timestamp: number; value: number }>;
    min?: number;
    max?: number;
    status?: 'normal' | 'warning' | 'critical';
  }>;
  selectedSpace: ThreeDModels;
  modelAttachment?: boolean;
  deviceIds?: string[];
  onDeviceSelect?: (deviceId: string, tabName?: string) => void;
  isEditorMode?: boolean;
  sensorTypeFilters?: string[];
  showHeatmap?: boolean;
}

// Use forwardRef to expose methods to parent
export const Scene = forwardRef<{ resetCameraView: () => void }, SceneProps>(
  (
    { sensorData, selectedSpace, modelAttachment = true, deviceIds = [], onDeviceSelect, isEditorMode = false, sensorTypeFilters, showHeatmap = false },
    ref,
  ) => {
    const groupRef = useRef<THREE.Group>(null);
    const spotlightRef = useRef<THREE.SpotLight>(null);
    const { camera } = useThree();
    const [initialCameraAdjusted, setInitialCameraAdjusted] = useState(false);
    const [showInfoPanel, setShowInfoPanel] = useState(true);
    const showAlertsPanel = true;

    // Fetch device alerts when deviceIds are provided
    const { data: alertsData } = useGetAlertsQuery(
      {
        deviceId: deviceIds.join(','),
        includeRule: 'true',
        limit: '10',
      },
      {
        skip: deviceIds.length === 0,
        pollingInterval: 30000, // Poll every 30 seconds
      },
    );

    // Fetch time series data for temperature and humidity sensors
    const { data: timeSeriesData } = useGetAnalyticsTimeSeriesQuery(
      {
        deviceIds: deviceIds.join(','),
        sensorTypes: 'temperature,humidity',
        interval: 'hour',
        limit: '1',
      },
      {
        skip: deviceIds.length === 0,
        pollingInterval: 5000, // Poll every 5 seconds
      }
    );

    // Process alerts data
    const deviceAlerts = useMemo(() => {
      const alerts = alertsData?.data?.items || [];
      return alerts.map((alert) => {
        // Find matching sensor data
        const matchingSensor = sensorData.find((s) => {
          // Safely check if deviceName exists and if it includes the device_pk
          return s.deviceName && alert.device_pk && s.deviceName.includes(alert.device_pk.toString());
        });

        // Find sensor by id
        const sensorById = sensorData.find((s) => s.id === alert.sensor_pk.toString());

        return {
          id: alert.id,
          deviceId: alert.device_pk.toString() || '',
          deviceName: matchingSensor?.deviceName || 'Unknown Device',
          sensorId: alert.sensor_pk.toString() || '',
          sensorName: sensorById?.name || 'Unknown Sensor',
          sensorType: sensorById?.type || '',
          severity: alert.severity || 'warning',
          message: alert.triggered_value?.toString() || 'Alert detected',
          status: alert.status || 'active',
          value: alert.triggered_value || 0,
          unit: sensorById?.unit || '',
          timestamp: alert.triggered_at || new Date().toISOString(),
          // Store this so we know which device to navigate to
          originalDeviceId: matchingSensor?.deviceId || alert.device_pk.toString() || '',
        };
      });
    }, [alertsData, sensorData]);

    // Get sensor alerts
    const sensorAlerts = useMemo(() => {
      return sensorData.filter((sensor) => sensor.status === 'warning' || sensor.status === 'critical');
    }, [sensorData]);

    // Process sensor data from time series
    const processedSensorData = useMemo(() => {
      if (!timeSeriesData?.data) return [];

      return timeSeriesData.data.map(series => {
        const latestReading = series.series[0];
        // Find matching sensor in original sensorData to get position
        const originalSensor = sensorData.find(s => s.id === series.sensor_id);
        return {
          id: series.sensor_id,
          name: series.sensor_name,
          type: series.sensor_type,
          value: Number((latestReading?.value || 0).toFixed(2)),
          unit: series.unit || '°C',
          isOnline: true,
          position: originalSensor?.position || [0, 0, 0],
          deviceName: series.device_name || 'Unknown Device',
          deviceId: series.device_id,
          timestamp: latestReading?.timestamp || new Date().toISOString(),
        };
      });
    }, [timeSeriesData, sensorData]);

    // Expose resetCameraView method to parent
    useImperativeHandle(ref, () => ({
      resetCameraView: () => {
        setInitialCameraAdjusted(false);
      },
    }));

    // Define model position based on the selected space
    const modelPosition: [number, number, number] =
      selectedSpace === ThreeDModels.EV_CHARGER ? [0, 0, -15] : [0, 0, -10];

    // Define model scale based on the selected space
    const modelScale = selectedSpace === ThreeDModels.EV_CHARGER ? 3 : 5;

    // Create sensor positions with organized layout and arrows
    const sensorAttachments = useMemo(() => {
      if (!modelAttachment) return {};

      // Group sensors by type
      const sensorsByType = sensorData.reduce(
        (acc, sensor) => {
          const type = sensor.type?.toLowerCase() || 'default';
          if (!acc[type]) acc[type] = [];
          acc[type].push(sensor);
          return acc;
        },
        {} as Record<string, typeof sensorData>,
      );

      // Get attachment points for sensors
      const attachPoints: Record<string, [number, number, number]> = {};

      // Assign attachment points by sensor type
      sensorData.forEach((sensor) => {
        const type = sensor.type?.toLowerCase() || 'default';
        const sameTypeIndex = sensorsByType[type]?.findIndex((s) => s.id === sensor.id) || 0;

        // Get appropriate attachment point on the model
        attachPoints[sensor.id] = getSensorAttachmentPoint(selectedSpace, type, modelPosition, sameTypeIndex);
      });

      return attachPoints;
    }, [sensorData, selectedSpace, modelPosition, modelAttachment]);

    // Get model-specific information and key metrics
    const modelInfo = useMemo(() => {
      const info: {
        title: string;
        description: string;
        metrics: Array<{
          name: string;
          value: string;
          status?: 'normal' | 'warning' | 'critical';
        }>;
      } = {
        title: selectedSpace,
        description: `3D visualization of ${selectedSpace}`,
        metrics: [],
      };

      // Add metrics based on sensor data
      sensorData.forEach((sensor) => {
        if (sensor.type && sensor.value !== undefined) {
          info.metrics.push({
            name: sensor.name,
            value: `${sensor.value}${sensor.unit || ''}`,
            status: sensor.status,
          });
        }
      });

      return info;
    }, [selectedSpace, sensorData]);

    // Camera positioning & animation code
    useEffect(() => {
      if (groupRef.current && !initialCameraAdjusted) {
        let optimalPosition: [number, number, number];
        let lookAtPoint: [number, number, number];

        if (selectedSpace === ThreeDModels.EV_CHARGER) {
          optimalPosition = [10, 10, 10];
          lookAtPoint = [0, 2, -5];
        } else {
          optimalPosition = [15, 12, 15];
          lookAtPoint = [0, 0, 0];
        }

        if (sensorData.length > 0) {
          const boundingBox = new THREE.Box3().setFromObject(groupRef.current);
          const center = new THREE.Vector3();
          boundingBox.getCenter(center);
          const size = new THREE.Vector3();
          boundingBox.getSize(size);

          const maxSize = Math.max(size.x, size.y, size.z);
          const fitDistance = maxSize / Math.tan((Math.PI * camera.fov) / 360);

          const modelCenter = new THREE.Vector3(...modelPosition);
          center.lerp(modelCenter, 0.4);

          lookAtPoint = [center.x, center.y + 2, center.z];
          optimalPosition = [center.x + fitDistance * 0.5, center.y + fitDistance * 0.5, center.z + fitDistance * 0.6];
        }

        camera.position.set(...optimalPosition);
        camera.lookAt(...lookAtPoint);
        camera.updateProjectionMatrix();

        setInitialCameraAdjusted(true);
      }
    }, [selectedSpace, initialCameraAdjusted, camera, modelPosition, sensorData.length]);

    // Animation for spotlight
    useFrame(() => {
      if (spotlightRef.current) {
        spotlightRef.current.position.x = camera.position.x * 0.8;
        spotlightRef.current.position.z = camera.position.z * 0.8;
        spotlightRef.current.position.y = camera.position.y * 0.8;

        spotlightRef.current.target.position.set(...modelPosition);
        spotlightRef.current.target.updateMatrixWorld();
      }
    });

    // Handle alert action
    const handleAlertAction = (deviceId: string) => {
      if (onDeviceSelect) {
        onDeviceSelect(deviceId, 'alerts');
      }
    };

    return (
      <>
        <color attach="background" args={['#1a1a1a']} />
        <fog attach="fog" args={['#1a1a1a', 30, 100]} />
        <ambientLight intensity={0.5} />
        <spotLight
          ref={spotlightRef}
          position={[10, 10, 10]}
          angle={0.3}
          penumbra={1}
          intensity={1}
          castShadow
        />
        <OrbitControls
          enablePan={!isEditorMode}
          enableZoom={true}
          enableRotate={!isEditorMode}
          minDistance={5}
          maxDistance={50}
          maxPolarAngle={Math.PI / 2}
        />
        <Stats />
        <GridBase />
        <Suspense fallback={<ModelLoadingFallback />}>
          <ModelLoader
            modelType={selectedSpace}
            position={modelPosition}
            scale={modelScale}
          />
        </Suspense>
        <DeviceCubes
          devices={deviceIds.map((id) => ({
            id,
            position: [0, 0, 0],
          }))}
          selectedDevices={[]}
          onDeviceClick={onDeviceSelect || (() => {})}
          isEditorMode={isEditorMode}
          sensorTypeFilters={sensorTypeFilters}
          showHeatmap={showHeatmap}
        />
        {showInfoPanel && (
          <InfoPanel
            position={[modelPosition[0] + 10, modelPosition[1] + 8, modelPosition[2]]}
            info={modelInfo}
            onClose={() => setShowInfoPanel(false)}
          />
        )}
        {showAlertsPanel && (
          <AlertsPanel
            position={[modelPosition[0] - 10, modelPosition[1] + 8, modelPosition[2]]}
            alerts={deviceAlerts}
            sensorAlerts={sensorAlerts}
            onClose={() => {}}
            onViewDetails={handleAlertAction}
            persistent={true}
          />
        )}
        <Html position={[modelPosition[0] + 10, modelPosition[1] + 8, modelPosition[2]]} transform>
          <button
            onClick={() => setShowInfoPanel(!showInfoPanel)}
            className="bg-background/90 backdrop-blur-md border border-border p-2 rounded-lg shadow-lg hover:bg-background/80 transition-colors"
            style={{ pointerEvents: 'auto' }}
          >
            {showInfoPanel ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </Html>
      </>
    );
  },
);

// Add display name for debugging
Scene.displayName = 'Scene';
